import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePremiumStatus } from "@/hooks/usePremiumStatus";
import { onWorkoutCompleted } from "@/lib/workoutEvents";
import { UpgradeSheet } from "@/components/monetization/UpgradeSheet";

const LAST_KEY = "moment_upsell_last";
const COOLDOWN_MS = 4 * 86400000;

/**
 * Shows the upgrade sheet at high-intent moments for free users:
 * - right after finishing a workout (max once every 4 days)
 * - when any feature fires `window.dispatchEvent(new CustomEvent("pro-limit-hit", { detail: { feature } }))`
 */
export function MomentUpsell() {
  const { user } = useAuth();
  const { isPremium, isLoading } = usePremiumStatus();
  const [state, setState] = useState<{ open: boolean; feature: string; title?: string; subtitle?: string }>({
    open: false,
    feature: "post_workout",
  });

  useEffect(() => {
    if (!user || isLoading || isPremium) return;

    const offDone = onWorkoutCompleted(() => {
      const last = Number(localStorage.getItem(LAST_KEY) || 0);
      if (Date.now() - last < COOLDOWN_MS) return;
      localStorage.setItem(LAST_KEY, String(Date.now()));
      setTimeout(() => {
        setState({
          open: true,
          feature: "post_workout",
          title: "Nice work! Keep the streak going",
          subtitle: "Let AI plan your next session around your recovery. 7 days free.",
        });
      }, 2500);
    });

    const onLimit = (e: Event) => {
      const feature = (e as CustomEvent).detail?.feature || "limit_hit";
      setState({
        open: true,
        feature: `limit_${feature}`,
        title: "You've hit the free limit",
        subtitle: "Go unlimited with Pro. Your first 7 days are free.",
      });
    };
    window.addEventListener("pro-limit-hit", onLimit);

    return () => {
      offDone();
      window.removeEventListener("pro-limit-hit", onLimit);
    };
  }, [user, isLoading, isPremium]);

  return (
    <UpgradeSheet
      open={state.open}
      onOpenChange={(open) => setState((s) => ({ ...s, open }))}
      feature={state.feature}
      title={state.title}
      subtitle={state.subtitle}
    />
  );
}
